import { Formatting } from '../../data/formatting';
import { DataStore } from '../../data/store';
import { HeatmapInfo, HeatmapColumn, HeatmapRow, HeatmapCell, HeatmapScale } from '../../design/charts/heatmap';
import { DataColumn, DataRow, DataRows, Entity } from '../../types';

type ValueRange = {
  from: number;
  to: number;
  scale: HeatmapScale;
};

function makeHeatmapData(
  data: DataStore,
  options: {
    xGrouping: string;
    yGrouping: string;
    sortX: boolean;
    sortY: boolean;
    kpi: string;
  }
): HeatmapInfo | undefined {
  const { xGrouping, yGrouping, kpi, sortX, sortY } = options;

  const kpiColumn = data.getDataColumns().find(col => col.name === kpi);
  const marketTotal = data.getGroupedData([])[0];
  const xTotals = data.getGroupedData([xGrouping])[0];
  const yTotals = data.getGroupedData([yGrouping])[0];
  const combinedRows = data.getGroupedData([xGrouping, yGrouping], true)[0];

  if (!xTotals || !yTotals || !combinedRows) {
    return;
  }

  const currency = data.getCurrency();
  const xIdentifiers = getRowsIdentifiers(xTotals.data, xGrouping, data.getDataColumns());
  const yIdentifiers = getRowsIdentifiers(yTotals.data, yGrouping, data.getDataColumns());

  if (sortX) {
    xIdentifiers.sort((a, b) => a.label.localeCompare(b.label));
  }
  if (sortY) {
    yIdentifiers.sort((a, b) => a.label.localeCompare(b.label));
  }

  const heatMapColumns: HeatmapColumn[] = [];
  heatMapColumns.push(
    {
      name: 'all',
      label: 'Total',
    },
    ...yIdentifiers.map(yId => ({
      name: yId.value,
      label: yId.label,
    }))
  );

  const heatMapRows: HeatmapRow[] = [];

  const xTotalRow: HeatmapRow = {
    name: 'all',
    label: 'Total',
    cells: [makeHeatmapCell(kpiColumn, 'all', 'all', currency, marketTotal.data[0])],
  };
  yIdentifiers.forEach(yId => {
    const yTotal = yTotals.data.find(row => String(row.values[yGrouping]) === yId.value);
    xTotalRow.cells.push(makeHeatmapCell(kpiColumn, 'all', yId.value, currency, yTotal));
  });

  heatMapRows.push(xTotalRow);

  xIdentifiers.forEach(xId => {
    const heatMapRow: HeatmapRow = {
      name: xId.value,
      label: xId.label,
      cells: [],
    };

    const xTotal = xTotals.data.find(row => String(row.values[xGrouping]) === xId.value);
    heatMapRow.cells.push(makeHeatmapCell(kpiColumn, xId.value, 'all', currency, xTotal));

    yIdentifiers.forEach(yId => {
      const combinedRow = combinedRows.data.find(
        row => String(row.values[xGrouping]) === xId.value && String(row.values[yGrouping]) === yId.value
      );
      heatMapRow.cells.push(makeHeatmapCell(kpiColumn, xId.value, yId.value, currency, combinedRow));
    });

    heatMapRows.push(heatMapRow);
  });

  let upper = 0;
  let lower = 0;
  heatMapRows.forEach(row => {
    row.cells.forEach(cell => {
      if (cell.raw > upper) {
        upper = cell.raw;
      }
      if (cell.raw < lower) {
        lower = cell.raw;
      }
    });
  });

  const ranges = makeScaledRanges(lower, upper);

  heatMapRows.forEach(row => {
    row.cells.forEach(cell => {
      if (cell.raw === 0) {
        cell.scale = HeatmapScale.neutral;
      } else {
        let range: ValueRange | undefined;
        if (cell.raw < 0) {
          range = ranges.find(r => cell.raw <= r.from && cell.raw >= r.to);
        } else {
          range = ranges.find(r => cell.raw >= r.from && cell.raw <= r.to);
        }
        cell.scale = range?.scale || HeatmapScale.neutral;
      }
    });
  });

  return {
    rows: heatMapRows,
    columns: heatMapColumns,
  };
}

function makeScaledRanges(lower: number, upper: number): ValueRange[] {
  const upperStep = upper / 4;
  const lowerStep = lower / 4;
  const lowerRanges: ValueRange[] = [];
  const upperRanges: ValueRange[] = [];

  for (let i = 1; i < 5; i++) {
    lowerRanges.push({
      from: (i - 1) * lowerStep,
      to: i * lowerStep,
      scale: `negative${i}` as HeatmapScale,
    });
    upperRanges.push({
      from: (i - 1) * upperStep,
      to: i * upperStep,
      scale: `positive${i}` as HeatmapScale,
    });
  }

  return [...lowerRanges, ...upperRanges];
}

function makeHeatmapCell(
  kpiColumn: DataColumn,
  xName: string,
  yName: string,
  currency: string,
  row?: DataRow
): HeatmapCell {
  if (!row) {
    return {
      isMissing: true,
      formatted: 'n.a',
      raw: 0,
      scale: HeatmapScale.neutral,
      xName,
      yName,
    };
  }

  const value = DataStore.getCellsLatestValue(row, kpiColumn.name);
  if (value === null) {
    return {
      isMissing: true,
      formatted: '-',
      raw: 0,
      scale: HeatmapScale.neutral,
      xName,
      yName,
    };
  }

  return {
    isMissing: false,
    formatted: Formatting.format(value, {
      dataType: kpiColumn.dataType,
      formatHint: kpiColumn.formatHint,
      isChange: kpiColumn.isChange,
      currency,
    }),
    raw: value,
    scale: HeatmapScale.negative4,
    xName,
    yName,
  };
}

function getRowsIdentifiers(rows: DataRows, field: string, columns: DataColumn[]): Entity[] {
  return rows.map(row => {
    const { id, name } = DataStore.getRowIdentifiers(row, columns, field);
    return {
      value: id,
      label: name,
    };
  });
}

export { makeHeatmapData };
